import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { CartProvider } from '@/contexts/CartContext';

import PublicLayout from '@/components/PublicLayout';
import RoleGuard from '@/components/RoleGuard';
import FounderConsoleLayout from '@/components/FounderConsoleLayout';
import AdminLayout from '@/components/AdminLayout';

// Public pages
import HomePage from '@/pages/HomePage';
import FoundersClubPage from '@/pages/FoundersClubPage';
import CollectionPage from '@/pages/CollectionPage';
import CatalogPage from '@/pages/CatalogPage';
import ProductDetailPage from '@/pages/ProductDetailPage';
import StorePage from '@/pages/StorePage';
import CheckoutPage from '@/pages/CheckoutPage';
import TrustBuyPage from '@/pages/TrustBuyPage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import JoinWizard from '@/pages/JoinWizard';
import PolicyPage from '@/pages/PolicyPage';
import SizeGuidePage from '@/pages/SizeGuidePage';
import SetPasswordPage from '@/pages/SetPasswordPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';

// Founder console pages
import FounderDashboard from '@/pages/founder/Dashboard';
import Bundle from '@/pages/founder/Bundle';
import DesignMyCollection from '@/pages/founder/DesignMyCollection';
import FounderColourway from '@/pages/founder/FounderColourway';
import Production from '@/pages/founder/Production';
import Updates from '@/pages/founder/Updates';
import Vault from '@/pages/founder/Vault';
import Pitches from '@/pages/founder/Pitches';
import Rewards from '@/pages/founder/Rewards';
import Account from '@/pages/founder/Account';
import FounderCatalog from '@/pages/founder/Catalog';
import FounderStore from '@/pages/founder/Store';
import FounderMyOrders from '@/pages/founder/MyOrders';

// Admin panels
import {
  AdminDashboard, AdminMembers, AdminApplications, AdminPayments,
  AdminProduction, AdminColourways, AdminProducts,
} from '@/pages/admin/AdminPanels';
import {
  AdminUpdates, AdminPitches, AdminDiscountCodes, AdminContent, AdminMessages, AdminReports,
} from '@/pages/admin/AdminPanels2';
import AdminEmails from '@/pages/admin/AdminEmails';
import AdminCatalog from '@/pages/admin/AdminCatalog';
import AdminTemplates from '@/pages/admin/AdminTemplates';
import AdminTiers from '@/pages/admin/AdminTiers';
import AdminFounderOrders from '@/pages/admin/AdminFounderOrders';
import AdminFounderContent from '@/pages/admin/AdminFounderContent';
import AdminCustomers from '@/pages/admin/AdminCustomers';
import AdminFoundersArchive from '@/pages/admin/AdminFoundersArchive';
import AdminShopDashboard from '@/pages/admin/AdminShopDashboard';
import AdminShopProducts from '@/pages/admin/AdminShopProducts';
import AdminShopOrders from '@/pages/admin/AdminShopOrders';
import AdminShopCustomers from '@/pages/admin/AdminShopCustomers';
import AdminShopContent from '@/pages/admin/AdminShopContent';
import AdminShopDiscounts from '@/pages/admin/AdminShopDiscounts';
import AdminShopShipping from '@/pages/admin/AdminShopShipping';
import AdminAmbassadors from '@/pages/admin/AdminAmbassadors';
import AdminAmbassadorApplications from '@/pages/admin/AdminAmbassadorApplications';
import AdminWardrobeReleases from '@/pages/admin/AdminWardrobeReleases';
import AdminPayouts from '@/pages/admin/AdminPayouts';
import AmbassadorApplyPage from '@/pages/AmbassadorApplyPage';
import AmbassadorLandingPage from '@/pages/AmbassadorLandingPage';

// Ambassador & Customer consoles
import AmbassadorConsole from '@/pages/AmbassadorConsole';
import CustomerConsole from '@/pages/CustomerConsole';

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/catalog/:slug" element={<ProductDetailPage />} />
              <Route path="/store" element={<StorePage />} />
              <Route path="/store/:slug" element={<ProductDetailPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/ambassador/apply" element={<AmbassadorApplyPage />} />
              <Route path="/ambassador/:code" element={<AmbassadorLandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/size-guide" element={<SizeGuidePage />} />
              <Route path="/info/size-guide" element={<SizeGuidePage />} />
              <Route path="/legal/:pageKey" element={<PolicyPage />} />
              <Route path="/info/:pageKey" element={<PolicyPage />} />
              {/* Legacy Founders Club routes redirect to home */}
              <Route path="/founders-club" element={<Navigate to="/" replace />} />
              <Route path="/join" element={<Navigate to="/" replace />} />
              <Route path="/trustbuy" element={<Navigate to="/" replace />} />
              <Route path="/collection" element={<Navigate to="/catalog" replace />} />
            </Route>

            {/* Founder Console */}
            <Route path="/console/founder" element={
              <RoleGuard roles={['founder', 'admin']}><FounderConsoleLayout /></RoleGuard>
            }>
              <Route index element={<FounderDashboard />} />
              <Route path="bundle" element={<Bundle />} />
              <Route path="design" element={<DesignMyCollection />} />
              <Route path="colourway" element={<FounderColourway />} />
              <Route path="production" element={<Production />} />
              <Route path="updates" element={<Updates />} />
              <Route path="vault" element={<Vault />} />
              <Route path="pitches" element={<Pitches />} />
              <Route path="rewards" element={<Rewards />} />
              <Route path="account" element={<Account />} />
              <Route path="catalog" element={<FounderCatalog />} />
              <Route path="store" element={<FounderStore />} />
              <Route path="orders" element={<FounderMyOrders />} />
              <Route path="orders/:orderId" element={<FounderMyOrders />} />
              <Route path="invoice" element={<div />} />
            </Route>

            {/* Admin Console */}
            <Route path="/admin" element={
              <RoleGuard roles={['admin']}><AdminLayout /></RoleGuard>
            }>
              <Route index element={<AdminDashboard />} />
              {/* Heritage Store CRM */}
              <Route path="store" element={<AdminShopDashboard />} />
              <Route path="store/products" element={<AdminShopProducts />} />
              <Route path="store/orders" element={<AdminShopOrders />} />
              <Route path="store/customers" element={<AdminShopCustomers />} />
              <Route path="store/discounts" element={<AdminShopDiscounts />} />
              <Route path="store/shipping" element={<AdminShopShipping />} />
              <Route path="store/content" element={<AdminShopContent />} />
              <Route path="ambassadors" element={<AdminAmbassadors />} />
              <Route path="ambassador-applications" element={<AdminAmbassadorApplications />} />
              <Route path="wardrobe-releases" element={<AdminWardrobeReleases />} />
              <Route path="payouts" element={<AdminPayouts />} />
              <Route path="members" element={<AdminMembers />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="founders-archive" element={<AdminFoundersArchive />} />
              <Route path="applications" element={<AdminApplications />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="production" element={<AdminProduction />} />
              <Route path="colourways" element={<AdminColourways />} />
              <Route path="tiers" element={<AdminTiers />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="catalog" element={<AdminCatalog />} />
              <Route path="orders" element={<AdminFounderOrders />} />
              <Route path="founder-content" element={<AdminFounderContent />} />
              <Route path="templates" element={<AdminTemplates />} />
              <Route path="updates" element={<AdminUpdates />} />
              <Route path="pitches" element={<AdminPitches />} />
              <Route path="discount-codes" element={<AdminDiscountCodes />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="emails" element={<AdminEmails />} />
              <Route path="reports" element={<AdminReports />} />
            </Route>

            {/* Ambassador */}
            <Route path="/console/ambassador" element={
              <RoleGuard roles={['ambassador', 'admin']}><AmbassadorConsole /></RoleGuard>
            } />
            {/* Customer */}
            <Route path="/console/customer" element={
              <RoleGuard roles={['member', 'admin']}><CustomerConsole /></RoleGuard>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
